import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from '@components/common/Button';
import { theme } from '@styles/theme';
import { ConsultationComposer, ErrorText } from './ConsultationComposer';
import { useConsultationChat } from './useConsultationChat';
import type { ConsultationScope } from '#types/consultation';

const Thread = styled.div`height: min(48vh, 440px); min-height: 180px; overflow-y: auto; overscroll-behavior: contain; padding: 12px; margin-bottom: 16px; border: 1px solid ${theme.colors.border.light}; border-radius: 12px; background: ${theme.colors.background.secondary};`;
const Bubble = styled.div<{ $own: boolean }>`max-width: 86%; width: fit-content; margin: 12px 0 12px ${p => p.$own ? 'auto' : '0'}; padding: 12px 16px; border-radius: 16px; border: 1px solid ${theme.colors.border.light}; background: ${p => p.$own ? '#eef2ff' : '#fff'}; p { margin: 6px 0; white-space: pre-wrap; overflow-wrap: anywhere; } small { color: ${theme.colors.text.secondary}; }`;
export const consultationTime = (time: number | null | undefined): string => time == null ? '—' : new Date(time).toLocaleString();

export function ConsultationChat({ scope, active = true }: { scope: ConsultationScope; active?: boolean }) {
  const { t } = useTranslation('consultation');
  const chat = useConsultationChat(scope, active);
  const scroll = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const prepend = useRef<{ height: number; top: number }>();
  const [newMessages, setNewMessages] = useState(false);
  const last = useRef<string>();
  useLayoutEffect(() => {
    const element = scroll.current;
    if (!element) return;
    if (prepend.current) {
      element.scrollTop = prepend.current.top + element.scrollHeight - prepend.current.height;
      prepend.current = undefined;
    } else if (stick.current) element.scrollTop = element.scrollHeight;
    else if (last.current !== chat.messages[chat.messages.length - 1]?.id) setNewMessages(true);
    last.current = chat.messages[chat.messages.length - 1]?.id;
  }, [chat.messages]);
  return <>
    {scope.conversationUuid && chat.conversation && <p>{chat.conversation.customerDisplayName} · {t(chat.conversation.replyStatus)} · {t('updated')}: {consultationTime(chat.conversation.updatedTime)}</p>}
    {chat.error && <ErrorText role="alert">{t('loadFailed')} <Button variant="ghost" onClick={() => void chat.refresh().catch(() => {})}>{t('retry')}</Button></ErrorText>}
    <Thread ref={scroll} role="log" aria-label={t('history')} aria-live="polite" onScroll={() => {
      const element = scroll.current!; stick.current = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
      if (stick.current) setNewMessages(false);
    }}>
      {chat.hasOlder && <Button variant="ghost" disabled={chat.busy} onClick={() => {
        const element = scroll.current!;
        prepend.current = { height: element.scrollHeight, top: element.scrollTop };
        void chat.loadOlder().catch(() => { prepend.current = undefined; });
      }}>{t('older')}</Button>}
      {!chat.messages.length && <p>{chat.busy ? t('loading') : chat.error ? t('loadFailed') : t('empty')}</p>}
      {chat.messages.map(message => <Bubble key={message.id} $own={message.senderType === (scope.conversationUuid ? 'MANAGER' : 'CUSTOMER')}>
        <small>{t(message.senderType === 'MANAGER' ? 'support' : scope.conversationUuid ? 'customer' : 'me')} · {consultationTime(message.createdTime)}</small>
        <p>{message.content}</p>
      </Bubble>)}
    </Thread>
    {newMessages && <Button variant="ghost" onClick={() => { stick.current = true; scroll.current?.scrollTo({ top: scroll.current.scrollHeight }); setNewMessages(false); }}>{t('newMessages')}</Button>}
    <ConsultationComposer scope={scope} disabled={!active || chat.conversation?.customerKind === 'UNAVAILABLE'} onSent={async sent => {
      stick.current = true; chat.appendSent(sent.message); await chat.refresh();
    }} />
  </>;
}
